import { useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'
import { api } from '@/lib/api'

export default function ResetPassword() {
  const [codes, setCodes] = useState(['', '', '', '', '', ''])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const location = useLocation()
  const email = (location.state as any)?.email || ''

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return
    const newCodes = [...codes]
    newCodes[index] = value
    setCodes(newCodes)
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResend = async () => {
    if (!email) return
    try {
      await api.auth.forgotPassword(email)
    } catch {}
  }

  const handleSubmit = async () => {
    const code = codes.join('')
    if (code.length !== 6) {
      setError('6 оронтой код оруулна уу')
      return
    }
    if (!newPassword) {
      setError('Шинэ нууц үг оруулна уу')
      return
    }
    if (newPassword.length < 6) {
      setError('Нууц үг хамгийн багадаа 6 тэмдэгт байна')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Нууц үг таарахгүй байна')
      return
    }
    setError('')
    setLoading(true)
    try {
      await api.auth.resetPassword({ email, code, newPassword })
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="w-full max-w-[550px] flex flex-col gap-8 items-center text-center">
          <div className="size-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="size-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-black">Нууц үг амжилттай солигдлоо</h1>
            <p className="text-sm text-gray-600">
              Шинэ нууц үгээрээ нэвтэрнэ үү.
            </p>
          </div>
          <Link
            to="/login"
            className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition text-center"
          >
            Нэвтрэх
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-[550px] flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-black">Нууц үг шинэчлэх</h1>
          <p className="text-sm text-gray-600">
            Бид таны <span className="font-bold text-black">{email}</span> хаяг руу 6 оронтой код илгээсэн. Кодоо оруулж шинэ нууц үг тохируулна уу.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        {/* OTP input */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Баталгаажуулах код</label>
          <div className="flex gap-3">
            {codes.map((code, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-full aspect-square max-w-[64px] bg-white border border-gray-200 rounded-xl text-center text-2xl font-semibold text-gray-900 shadow-xs outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition"
              />
            ))}
          </div>
          <button
            onClick={handleResend}
            className="text-sm font-medium text-primary-500 hover:text-primary-600 self-start mt-1"
          >
            Код дахин илгээх
          </button>
        </div>

        {/* New password */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Шинэ нууц үг</label>
            <div className="bg-white border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition">
              <Lock className="size-[18px] text-gray-400 shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <Eye className="size-[18px]" /> : <EyeOff className="size-[18px]" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Нууц үг давтах</label>
            <div className="bg-white border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition">
              <Lock className="size-[18px] text-gray-400 shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50"
        >
          {loading ? 'Хадгалж байна...' : 'Нууц үг шинэчлэх'}
        </button>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="size-5" />
          Нэвтрэх хуудас руу буцах
        </Link>
      </div>
    </AuthLayout>
  )
}
